/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

// Imports to fix errors
import "@babel/core"

// Normal imports
import Vue from 'vue';
import moment from 'moment';
import App from './App';
import store from '~/store';
import router from '~/router';
import { ServerTable } from 'vue-tables-2';
import {
    CreateCreditInvoiceButton,
    SalesInvoiceReminderButton,
    MarkInvoiceAsPaidButton,
} from '~/components/buttons'
import FormAddress from '~/components/forms/address/form.vue'
import PhoneNumber from '~/Form/elements/phone-number.vue'
import FormTextarea from '~/Form/elements/textarea.vue'
import FormRadio from '~/Form/elements/radio.vue'
import FormInput from '~/Form/elements/input.vue'
import FromCheckBox from '~/Form/elements/checkbox.vue'
import FormSelect from '~/Form/elements/select.vue'
import Dropzone from '~/Form/elements/dropzone.vue'
import FormDateRangePicker from '~/components/forms/daterange-picker.vue'
import DatePicker from '~/Form/elements/date-picker.vue'
import ModuleAddress from '~/components/forms/address/module.vue'
import ModalLocation from '~/components/modals/location.vue'
import ActiveSelect from '~/components/elements/selects/active'
import SalutationSelect from '~/components/elements/selects/salutation'
import BackendSelect from '~/components/elements/selects/select'
import Tel from '~/components/helpers/tel'
import DownloadFileLink from '~/components/helpers/download-file-link'
import AddressCard from '~/components/cards/address-card'
import Mail from '~/components/helpers/mail'
import Address from '~/components/helpers/address'
import FormFile from "bootstrap-vue/lib/components/form-file"
import ATelHref from '~/directives/a-tel-href'
import VClickOutside from '~/directives/v-click-outside'
import AMailToHref from '~/directives/a-mail-to-href'
import { LinkIfPermissions, LinkIf } from '~/components/helpers'
import FilePreview from '~/components/elements/file-preview'

// Date picker
import VuePikaday from '@enrian/vue-pikaday';
import '@enrian/vue-pikaday/dist/vue-pikaday.min.css';
Vue.use(VuePikaday);

// Fancy spinner
var BeatLoader = require('vue-spinner/dist/vue-spinner.min').BeatLoader
Vue.component('beat-loader', BeatLoader);

// Portals
import PortalVue from 'portal-vue'
Vue.use(PortalVue)

// NL2BR
import Nl2br from 'vue-nl2br'
Vue.component('nl2br', Nl2br)

// Toggle button
import { ToggleButton } from 'vue-js-toggle-button'
Vue.component('ToggleButton', ToggleButton)

// Notifications
import Snotify, { SnotifyPosition } from 'vue-snotify'
Vue.use(Snotify, {
    toast: {
        position: SnotifyPosition.rightTop,
        timeout: 6000,
    }
});

Vue.use(ServerTable, {}, false);
Vue.component('link-if-permissions', LinkIfPermissions);
Vue.component('download-file-link', DownloadFileLink);
Vue.component('link-if', LinkIf);
Vue.component('mark-invoice-as-paid-button', MarkInvoiceAsPaidButton);
Vue.component('create-credit-invoice-button', CreateCreditInvoiceButton);
Vue.component('sales-invoice-reminder-button', SalesInvoiceReminderButton);
Vue.component('form-file', FormFile);
Vue.component('dropzone', Dropzone);
Vue.component('address-card', AddressCard);
Vue.component('backend-select', BackendSelect);
Vue.component('tel', Tel);
Vue.component('mail', Mail);
Vue.component('address-helper', Address);
Vue.component('salutation-select', SalutationSelect);
Vue.component('active-select', ActiveSelect);
Vue.component('phone-number', PhoneNumber);
Vue.component('form-address', FormAddress);
Vue.component('form-textarea', FormTextarea);
Vue.component('form-radio', FormRadio);
Vue.component('form-input', FormInput);
Vue.component('form-checkbox', FromCheckBox);
Vue.component('form-select', FormSelect);
Vue.component('form-date-picker', DatePicker);
Vue.component('form-daterange-picker', FormDateRangePicker);
Vue.component('module-address', ModuleAddress);
Vue.component('modal-location', ModalLocation);
Vue.component('file-preview', FilePreview);

Vue.directive('tel', ATelHref);
Vue.directive('mail-to', AMailToHref);
Vue.directive('click-outside', VClickOutside);

Vue.filter('date', function (value) {
    if (!value || value === '-') {
        return '-';
    }

    value = value.toString();

    if (! moment(value).isValid()) {
        return value;
    }

    return moment(value).locale(window.config.locale).format(window.config.date_format);
});

Vue.filter('datetime', function (value) {
    if (!value || value === '-') {
        return '-';
    }

    value = value.toString();

    if (! moment(value).isValid()) {
        return value;
    }

    return moment(value).locale(window.config.locale).format(window.config.datetime_format);
});

Vue.filter('dateForHumans', function (value) {
    if (!value || value === '-') {
        return '-';
    }

    value = value.toString();

    if (! moment(value).isValid()) {
        return value;
    }

    return moment(value).locale(window.config.locale).fromNow();
});

/**
 * Checks if the user has the right permissions.
 *
 * @param permission
 * @returns {boolean}
 */
window.$can = Vue.prototype.$can = (permission) => {
    return store.getters.authUser !== null && store.getters.authUser.can[permission]
};

/**
 * Checks if the user has the right role.
 *
 * @param role
 * @returns {boolean}
 */
window.$hasRole = Vue.prototype.$hasRole = (role) => {
    return store.getters.authUser !== null
        && store.getters.authUser.all_roles.filter(tempRole => tempRole.system_name == role).length > 0
};

/**
 * Checks if the user has the right type.
 *
 * @param type
 * @returns {boolean}
 */
window.$hasType = Vue.prototype.$hasType = (type) => {
    return store.getters.authUser !== null
        && store.getters.authUser.type.system_name == type
};

/**
 * Function to get a property from objects/arrays
 * @param  Object   obj
 * @param  array    props
 * @param  mixed    fallback
 * @return mixed
 */
window.$getIfExists = Vue.prototype.$getIfExists = function (obj, props, fallback = '') {
    if (typeof props === 'string') {
        props = props.split('.')
    }

    if (typeof props === 'undefined') {
        console.error('$getIfExists requires the props parameter')

        return fallback
    }

    for (var i = 0; i < props.length; i++) {
        if (!obj || !obj.hasOwnProperty(props[i])) {
            return fallback;
        }
        obj = obj[props[i]];
    }

    return obj;
}

import Http from '~/Http';
window.axios = Http;

window._ = require('lodash');
window.moment = Vue.prototype.moment = require('moment');
moment.locale('nl');

/**
 * Get the translation for a certain string from the backend.
 *
 * @param key
 * @param replace
 * @return string
 */
window.lang = Vue.prototype.lang = (key, replace) => {
    return langHolderReplace(_.get(window.trans, key, key), replace);
};

/**
 * Add parameters to the translated string.
 *
 * @param translated
 * @param replace
 * @returns string
 */
window.langHolderReplace = (translated, replace) => {
    for (var key in replace) {
        translated = translated.replace(':' + key, replace[key]);
    }

    return translated;
};

/**
 * Get the translation for a certain string from the backend with the first letter being an uppercase.
 *
 * @param key
 * @param replace
 * @returns string
 */
window.uLang = Vue.prototype.uLang = (key, replace) => {

    let translation = _.get(window.trans, key, key);

    if (typeof translation === "undefined") {
        return "-- Localization key: [" + key + "] not found --";
    }

    let translated = langHolderReplace(translation, replace);

    return translated.charAt(0).toUpperCase() + translated.slice(1);
};


/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */
window.vue = new Vue({
    router,
    store,
    template: '<App/>',
    components: {
        App
    }
}).$mount('#app');

window.randomString = (len, charSet) => {
    charSet = charSet || 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let randomString = '';

    for (let i = 0; i < len; i++) {
        let randomPoz = Math.floor(Math.random() * charSet.length);
        randomString += charSet.substring(randomPoz, randomPoz+1);
    }
    return randomString;
};

window.slugify = text => {
    return text.toString().toLowerCase()
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
        .replace(/\-\-+/g, '-')         // Replace multiple - with single -
        .replace(/^-+/, '')             // Trim - from start of text
        .replace(/-+$/, '');            // Trim - from end of text
};

window.filterInt = function(value) {
    if (/^(\-|\+)?([0-9]+|Infinity)$/.test(value))
        return Number(value);
    return NaN;
};

window.getDepartmentById = function (id) {
    return window.config.departments.find((d) => d.id === id);
};

// Get nested value from array/object or return null
window.$getOrNull = Vue.prototype.$getOrNull =
    (needle, haystack) => needle.reduce(
        (path, next) => (path && path[next]) ? path[next] : null,
        haystack
    )

window.$filledOrNull = Vue.prototype.$filledOrNull = (array) => Array.isArray(array) && array.length ? array : null;

window.$parseStringify = Vue.prototype.$parseStringify = (array) => JSON.parse(JSON.stringify(array))
